// @flow

import {
  type UserInputValidationError,
  type UserInputValidatorArgs,
  onlyWhenSupport,
  VALID,
} from '@tectonics/prs/validation';

import { getCurrentFeatures } from '@tectonics/prs/payment-spec';
import { getCurrentPaymentMethodOptionalFields, hasOptionalField } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

import { validateNigerianPhoneNumber } from './nigerian-phone-number';
import { validateNigerianBankCode } from '@tectonics/prs/nigeria/validation/nigerian-bank-code';
import { validateNigerianAccountNumber } from '@tectonics/prs/nigeria/validation/nigerian-account-number';
import { validateRequiredDirectDebitField } from '@tectonics/prs/nigeria/validation/direct-debit-required-field';
import { validateNigerianEWallet } from '@tectonics/prs/nigeria/validation/nigerian-ewallet';
import { validateCustomerEmail } from '@tectonics/prs/core/customer/validation/customer-email';
import { validateNigerianCustomerName } from '@tectonics/prs/nigeria/validation/nigerian-customer-name';
import { shouldDisabledCustomerNameField } from '@tectonics/prs/ui/get-disabled-customer-name-field';

export function validator({
  spec,
  prsSupport,
  userInputName,
  userInputValue,
  userInputValues,
  incomingPayment,
}: UserInputValidatorArgs): ?UserInputValidationError {
  const currentFeatures = getCurrentFeatures(spec, userInputValues['selectedPaymentMethod']);
  const onlyWhen = onlyWhenSupport(currentFeatures, prsSupport);
  let optionalFields = incomingPayment.status === 'OP' ? getCurrentPaymentMethodOptionalFields(incomingPayment, userInputValues, spec) : [];

  switch (userInputName) {
    case 'customerName':
      if (hasOptionalField(optionalFields, 'name')) {
        return VALID;
      }
      return onlyWhen('field_nigerian_customer_name') && validateNigerianCustomerName(
        userInputValues.selectedPaymentMethod,
        userInputValues.selectedEWallet,
        userInputValue,
        incomingPayment.status === 'OP' && shouldDisabledCustomerNameField(incomingPayment)
      );

    case 'nigerianPhoneNumber':
      if (!hasOptionalField(optionalFields, 'telephone')) {
        return onlyWhen('field_nigerian_phone_number') && validateNigerianPhoneNumber(userInputValue);
      }
      return null;

    case 'customerEmail':
      if (hasOptionalField(optionalFields, 'email')) {
        return null;
      }
      return onlyWhen('field_nigerian_customer_email') && validateCustomerEmail(userInputValue);

    case 'selectedEWallet':
      return validateNigerianEWallet(
        userInputValues.selectedPaymentMethod,
        userInputValues.selectedEWallet
      );

    case 'selectedBankCode':
      return validateNigerianBankCode(
        userInputValues.selectedPaymentMethod,
        userInputValues.selectedEWallet,
        userInputValues.selectedBankCode,
        incomingPayment
      );

    case 'nigerianAccountNumber':
      return validateNigerianAccountNumber(
        userInputValues.selectedPaymentMethod,
        userInputValues.selectedEWallet,
        userInputValues.nigerianAccountNumber
      );

    case 'nigerianAddress':
      return validateRequiredDirectDebitField(
        userInputValues.selectedPaymentMethod,
        userInputValues.selectedEWallet,
        userInputValues.nigerianAddress
      );

    default:
      return VALID;
  }
}
